import { createSelector, createStructuredSelector } from "reselect"

import Immutable from "immutable"
import { isInvalid, isSubmitting } from "redux-form/immutable"
import moment from "moment"
import {
    LOG_INTERACTION_FORM_KEY,
    LOG_RELATIONSHIP_FORM_KEY,
    LOG_COMMENT_FORM_KEY,
    LOG_DONATION_FORM_KEY,
} from "shared/imports/sharedConstants"
import { selectCustomFieldsConnection } from "shared/customFields/selectors"
import { checkPermission, permissionFilter } from "shared/imports/permissionFunctions"
import { getUserdataField } from "shared/imports/userdataFunctions"
import {
    createOrUpdateInteraction,
    createOrUpdateRelationship,
    createOrUpdateComment,
} from "shared/forms/LogInteraction/action-creators"
import {
    OfficialFilterableListView,
    OfficialsAndStaffEUFilterableListView,
} from "shared/search/containers/FilterableListView/FilterableListViews"
import { selectTeams } from "shared/userdata/userdata-selectors"
import { snakeToCamelCase } from "shared/imports/sharedHelperFunctions"

/**
 *  Note: this selector works on two slices of state - the 'form' slice for the redux-form
 *        and the 'interactionLogger' slice which holds the loaded team choices, project choices, and
 *        existing note (if form is being used to edit a note). All of the form value are within the 'form' silce.
 *
 *  The 'tag_dict' selector logic is commented out for now with TODO's for when we add custom tags to notes
 */

export const formDetailsDict = {
    [LOG_INTERACTION_FORM_KEY]: {
        createFunction: createOrUpdateInteraction,
        verb: "Log",
        name: "Interaction",
    },
    [LOG_DONATION_FORM_KEY]: {
        createFunction: createOrUpdateInteraction,
        verb: "Record",
        name: "Donation",
    },
    [LOG_RELATIONSHIP_FORM_KEY]: {
        createFunction: createOrUpdateRelationship,
        verb: "Create",
        name: "Relationship",
    },
    [LOG_COMMENT_FORM_KEY]: {
        createFunction: createOrUpdateComment,
        verb: "Write",
        name: "Comment",
    },
}

// selecting from redux form

export const selectForm = (state) => state.form

export const selectInteractionLogger = (state) => state.interactionLogger

// select either LOG_INTERACTION_FORM_KEY, _RELATIONSHIP_, _COMMENT_, or _DONATION_
export const createSelectLogInteractionForm = (reduxFormKey) =>
    createSelector(selectForm, (form) => form.get(reduxFormKey, Immutable.fromJS({})))

export const createSelectFormValues = (reduxFormKey) =>
    createSelector(createSelectLogInteractionForm(reduxFormKey), (logIntForm) =>
        logIntForm.get("values", Immutable.fromJS({})),
    )

export const selectArrayValue = (reduxFormKey, key) =>
    createSelector(createSelectFormValues(reduxFormKey), (formValues) =>
        formValues.get(key, []).toJS ? formValues.get(key, []).toJS() : formValues.get(key, []),
    )

export const selectLogInteractionFormValue = (key) =>
    createSelector(createSelectFormValues(LOG_INTERACTION_FORM_KEY), (formValues) =>
        formValues.get(key, undefined) && formValues.get(key, undefined).toJS
            ? formValues.get(key, undefined).toJS()
            : formValues.get(key, undefined),
    )

export const createSelectOfficialParticipants = (reduxFormKey) => selectArrayValue(reduxFormKey, "officials")
export const createSelectStafferParticipants = (reduxFormKey) => selectArrayValue(reduxFormKey, "staff")
export const createSelectPressContactParticipants = (reduxFormKey) => selectArrayValue(reduxFormKey, "pressContacts")
export const createSelectSupporterParticipants = (reduxFormKey) => selectArrayValue(reduxFormKey, "contacts")
export const createSelectTeamMemberParticipants = (reduxFormKey) => selectArrayValue(reduxFormKey, "teamMembers")
export const createSelectPublicOrgParticipants = (reduxFormKey) => selectArrayValue(reduxFormKey, "orgs")

// Selectors for recording UserActions
export const createSelectOfficialsMostRecentRegion = (reduxFormKey) =>
    selectArrayValue(reduxFormKey, "officials_most_recent_region")
export const createSelectStaffersMostRecentRegion = (reduxFormKey) =>
    selectArrayValue(reduxFormKey, "staffers_most_recent_region")
export const createSelectPressContactsMostRecentRegion = (reduxFormKey) =>
    selectArrayValue(reduxFormKey, "press_contacts_most_recent_region")
export const createSelectContactsRegion = (reduxFormKey) => selectArrayValue(reduxFormKey, "contacts_region")
export const createSelectCommentsRegion = (reduxFormKey) => selectArrayValue(reduxFormKey, "comment_regions")

export const createSelectBills = (reduxFormKey) =>
    createSelector(createSelectFormValues(reduxFormKey), (formValues) =>
        formValues.get("bills", []).toJS ? formValues.get("bills", []).toJS() : formValues.get("bills", []),
    )

export const createSelectFormErrors = (reduxFormKey) =>
    createSelector(createSelectLogInteractionForm(reduxFormKey), (logIntForm) => logIntForm.get("syncErrors", {}))

export const createSelectType = (reduxFormKey) =>
    createSelector(createSelectFormValues(reduxFormKey), (formValues) => formValues.get("type"))

// Specific to Mobile
export const selectBusinessCards = createSelector(
    createSelectFormValues(LOG_INTERACTION_FORM_KEY), // TODO - biz card scanner on any others?
    (logInteraction) =>
        (logInteraction && logInteraction.get("businessCards") && logInteraction.get("businessCards").toJS()) || [],
)

// Specific to Comment form
export const selectCommentItems = createSelector(createSelectFormValues(LOG_COMMENT_FORM_KEY), (formValues) =>
    formValues.get("commentItems", []).toJS
        ? formValues.get("commentItems", []).toJS()
        : formValues.get("commentItems", []),
)

export const createSelectDatetime = (formKey) =>
    createSelector(createSelectFormValues(formKey), (formValues) => formValues.get("datetime", undefined))

// selecting from interactionLogger slice (for resourceGetList calls, etc.)

const noteTypeFromKey = {
    [LOG_INTERACTION_FORM_KEY]: "interaction",
    [LOG_RELATIONSHIP_FORM_KEY]: "relationship",
    [LOG_COMMENT_FORM_KEY]: "comment",
    [LOG_DONATION_FORM_KEY]: "donation",
}
export const createSelectNoteDetails = (formKey) =>
    createSelector(selectInteractionLogger, (state) => state.get(noteTypeFromKey[formKey]))

export const selectProjectOptions = createSelector(selectInteractionLogger, (state) =>
    state.get("projectOptions", Immutable.fromJS([])).toJS(),
)

export const selectIsLoading = createSelector(selectInteractionLogger, (state) => state.get("isLoading"))

export const selectIsEditing = createSelector(selectInteractionLogger, (state) => state.get("isEditing"))

export const createSelectIsInvalid = (reduxFormKey) => (state) => isInvalid(reduxFormKey)(state)

export const createSelectIsSubmitting = (reduxFormKey) => (state) => isSubmitting(reduxFormKey)(state)

// if id exists, then we are updating an existing interaction, otherwise we are creating a new note
export const selectIsEdit = (state, props) =>
    Boolean(
        // check note profile for mobile
        (props.datumId && props.resource) ||
            // check params for web
            (props.params && props.params.datumId !== undefined),
    )

export const selectDatumId = (state, props) =>
    // check props.datumId for mobile, and props.params for web
    selectIsEdit(state, props) ? props.datumId || (props.params && props.params.datumId) : undefined
export const donationTypeValues = [
    DjangIO.app.userdata.types.InteractionType.donation_given.value,
    DjangIO.app.userdata.types.InteractionType.donation_received.value,
    DjangIO.app.userdata.types.InteractionType.intended_donation.value,
    DjangIO.app.userdata.types.InteractionType.sponsorship.value,
    DjangIO.app.userdata.types.InteractionType.grant.value,
]

/**
 * Returns all of the types that an interaction note can be (all InteractionType - donations)
 * @return {Array} of types with a 'value' and 'label'
 */
export const getInteractionTypeChoices = () =>
    Userdata.custom_interaction_types_for_org.map((type) => {
        const { id: value, title: label, ..._type } = type
        return {
            value,
            label,
            editable: true,
            loggable: true,
            mobile_order: _type.order,
            ..._type,
        }
    })

export const getRelationshipTypeChoices = () =>
    Userdata.internal_relationship_types.map((typeValue) => ({
        ...DjangIO.app.userdata.types.RelationshipType.by_value(typeValue),
        label: DjangIO.app.userdata.types.RelationshipType.by_value(typeValue).plural_label,
    }))

export const getDonationTypeChoices = () =>
    DjangIO.app.userdata.types.InteractionType.items()
        .filter(
            (typeValue) =>
                DjangIO.app.userdata.types.InteractionType.by_value(typeValue.value).should_display_amount &&
                DjangIO.app.userdata.types.InteractionType.by_value(typeValue.value).loggable,
        )
        .map((typeValue) => ({
            ...DjangIO.app.userdata.types.InteractionType.by_value(typeValue.value),
            label: DjangIO.app.userdata.types.InteractionType.by_value(typeValue.value).label,
        }))

export const getCommentTypeChoices = () =>
    DjangIO.app.models.QuorumDataType.items()
        // grab all QDTs that allow comments
        .filter((item) => item.comments)
        // then filter by permissions
        .filter((quorumDataType) =>
            permissionFilter(quorumDataType, { regions: quorumDataType.supported_regions, useCurrentRegions: true }),
        )
        .filter((qdt) => {
            if (qdt.value === DjangIO.app.models.QuorumDataType.project.value) {
                // TODO: figure if its safe to remove this ff__hideProjects
                return false
            }
            return true
        })
        // set label to qdt.singular
        .map((quorumDataType) => ({
            ...quorumDataType,
            label: quorumDataType.singular,
        }))

// if this is a comment about anything other than projects, then the user can tag porjects to this note
export const selectCanTagProjectsToComment = createSelector(
    createSelectType(LOG_COMMENT_FORM_KEY),
    (type) => type !== DjangIO.app.models.QuorumDataType.project.value,
)

/**
 * Returns all of the PossibleDurations in presentation order
 * @return {Array} of duration types with a 'value' and 'label'
 */
export const getDurationChoices = () =>
    DjangIO.app.userdata.types.PossibleDurations.items()
        .filter((item) => item.key !== "none")
        .sort((a, b) => a.order - b.order)
        .map((item) => ({
            ...item,
            label: item.label,
        }))

export const selectDefaultCustomFields = createSelector(
    selectCustomFieldsConnection(DjangIO.app.models.QuorumDataType.note.value),
    (customFields) => {
        if (!customFields || !customFields.customFields || !customFields.customFields.length) {
            return {}
        }

        return customFields.customFields.reduce((curr, next) => {
            if (next.default_value || next.default_value === false || next.default_value === 0) {
                curr[next.slug] = next.default_value
            }
            return curr
        }, {})
    },
)

// returns list of ids, handles case that cache does not contain field as a key (returns empty list)
const getFieldIdsFromCache = (noteDetails, field) => (noteDetails.cache_json[field] || []).map((p) => p.id)
const getFieldIdsFromProjects = (projects) =>
    projects && projects.length
        ? // ["/api/project/2/", "/api/project/3/"]
          projects.map((issue) => DjangIO.app.userdata.models.Project.idFromResourceUri(issue))
        : // []
          projects

// initial redux form values
export const initialInteractionFormValues = createSelector(
    selectInteractionLogger,
    selectDefaultCustomFields,
    (state, defaultCustomFields) => {
        const noteDetails = state.get("interaction")

        // if we loaded an existing note, then use that to create our form's initial values
        if (noteDetails !== undefined) {
            return {
                noteDetails,
                // We'll collate this just in case, but there shouldn't be any notes without a CustomInteractionType
                type: DjangIO.app.userdata.notes.models.CustomInteractionType.idFromResourceUri(
                    noteDetails.custom_interaction_type,
                ),
                // Set datetime to 'searchable_date' displayed on inline
                datetime: moment(noteDetails.date),
                date: moment(noteDetails.date),
                time: moment(noteDetails.date),
                duration: noteDetails.duration,
                noteText: noteDetails.text,
                projects: getFieldIdsFromProjects(noteDetails.projects),
                // SearchifyFields
                officials: noteDetails.officials,
                staff: noteDetails.staffers,
                pressContacts: noteDetails.pressContacts,
                teamMembers: getFieldIdsFromCache(noteDetails, "team_members"),
                contacts: getFieldIdsFromCache(noteDetails, "supporters"),
                orgs: getFieldIdsFromCache(noteDetails, "public_organizations"),
                attachments: noteDetails.cache_json.attachments,
                bills: getFieldIdsFromCache(noteDetails, "bills"),
                ...(noteDetails.tag_dict || {}),
                is_visible_to_partners: noteDetails.is_visible_to_partners,
                limited_to_teams: noteDetails.limited_to_teams || [],
            }
        }
        // otherwise, we are making a new interaction note from scratch
        const type = getInteractionTypeChoices().length
            ? getInteractionTypeChoices()[0].value
            : DjangIO.app.userdata.types.InteractionType.meeting.value
        return {
            type,
            duration: DjangIO.app.userdata.types.PossibleDurations.thirty_minutes.value,
            is_visible_to_partners: getUserdataField("should_share_with_partners_by_default"),
            limited_to_teams: getUserdataField("default_limited_to_teams"),
            projects: [],
            // SearchifyFields
            officials: [],
            staff: [],
            pressContacts: [],
            // default add current user as a teamMember
            teamMembers: [Userdata.id],
            contacts: [],
            orgs: [],
            attachments: [],
            bills: [],
            date: new Date(),
            time: new Date(),
            ...defaultCustomFields,
        }
    },
)

export const initialDonationFormValues = createSelector(
    selectInteractionLogger,
    selectDefaultCustomFields,
    (state, defaultCustomFields) => {
        const noteDetails = state.get("donation")

        // if we loaded an existing note, then use that to create our form's initial values
        if (noteDetails !== undefined) {
            return {
                noteDetails,
                isDonation: true,
                type: noteDetails.cache_json.interaction_type,
                datetime: moment(noteDetails.date, moment.ISO_8601),
                checkNumber: noteDetails.check_number,
                amount: noteDetails.amount || 0,
                noteText: noteDetails.text,
                projects: getFieldIdsFromProjects(noteDetails.projects),
                // SearchifyFields
                officials: noteDetails.officials,
                staff: noteDetails.staffers,
                teamMembers: getFieldIdsFromCache(noteDetails, "team_members"),
                contacts: getFieldIdsFromCache(noteDetails, "supporters"),
                orgs: getFieldIdsFromCache(noteDetails, "public_organizations"),
                attachments: noteDetails.cache_json.attachments,
                ...(noteDetails.tag_dict || {}),
                is_visible_to_partners: noteDetails.is_visible_to_partners,
                limited_to_teams: noteDetails.limited_to_teams || [],
            }
        }

        // otherwise, we are making a new interaction note from scratch
        const type = getDonationTypeChoices().length
            ? getDonationTypeChoices()[0].value
            : DjangIO.app.userdata.types.InteractionType.donation_given.value
        return {
            isDonation: true,
            type,
            is_visible_to_partners: getUserdataField("should_share_with_partners_by_default"),
            limited_to_teams: getUserdataField("default_limited_to_teams"),
            projects: [],

            // SearchifyFields
            officials: [],
            staff: [],
            // default add current user as a teamMember
            teamMembers: [Userdata.id],
            contacts: [],
            orgs: [],
            attachments: [],
            ...defaultCustomFields,
        }
    },
)

export const initialRelationshipFormValues = createSelector(
    selectInteractionLogger,
    selectDefaultCustomFields,
    (state, defaultCustomFields) => {
        const noteDetails = state.get("relationship")

        // if we loaded an existing note, then use that to create our form's initial values
        if (noteDetails !== undefined) {
            return {
                noteDetails,
                type: noteDetails.cache_json.relationship_type,
                status: noteDetails.relationship_status,
                noteText: noteDetails.text,
                attachments: noteDetails.cache_json.attachments,
                projects: getFieldIdsFromProjects(noteDetails.projects),
                // SearchifyFields
                officials: noteDetails.officials,
                staff: noteDetails.staffers,
                pressContacts: noteDetails.pressContacts,
                teamMembers: getFieldIdsFromCache(noteDetails, "team_members"),
                contacts: getFieldIdsFromCache(noteDetails, "supporters"),
                orgs: getFieldIdsFromCache(noteDetails, "public_organizations"),
                ...(noteDetails.tag_dict || {}),
                is_visible_to_partners: noteDetails.is_visible_to_partners,
                limited_to_teams: noteDetails.limited_to_teams || [],
            }
        }

        // otherwise, we are making a new interaction note from scratch
        const type = getRelationshipTypeChoices().length
            ? getRelationshipTypeChoices()[0].value
            : DjangIO.app.userdata.types.RelationshipType.professional.value
        return {
            attachments: [],
            is_visible_to_partners: getUserdataField("should_share_with_partners_by_default"),
            limited_to_teams: getUserdataField("default_limited_to_teams"),
            projects: [],
            status: true,
            type,
            // SearchifyFields
            officials: [],
            staff: [],
            pressContacts: [],
            teamMembers: [Userdata.id],
            contacts: [],
            orgs: [],
            ...defaultCustomFields,
        }
    },
)

const getCacheJsonKeyIds = (noteDetails, key) => {
    if (noteDetails && noteDetails.cache_json && noteDetails.cache_json[key]) {
        return noteDetails.cache_json[key].map((p) => p.id)
    } else {
        return []
    }
}

export const selectOfficialLabel = () => DjangIO.app.models.QuorumDataType.person.plural

export const selectOfficialPlaceholder = () =>
    `Choose multiple ${DjangIO.app.models.QuorumDataType.person.plural.toLowerCase()}...`

export const selectOfficialFilterableListView = () =>
    Userdata.has_international_region ? OfficialsAndStaffEUFilterableListView : OfficialFilterableListView

export const selectPermissionForOfficials = () => checkPermission(DjangIO.app.models.PermissionType.legislators)
export const selectPermissionForStaffers = () =>
    checkPermission(DjangIO.app.models.PermissionType.staffers) && !Userdata.has_international_region
export const selectPermissionForPressContacts = () => checkPermission(DjangIO.app.models.PermissionType.media_relations)
export const selectPermissionForCustomContacts = () =>
    checkPermission(DjangIO.app.models.PermissionType.custom_contacts)
export const selectPermissionForTaggingBill = () => checkPermission(DjangIO.app.models.PermissionType.legislative_data)
export const selectPermissionForExecutive = () => checkPermission(DjangIO.app.models.PermissionType.executives)
export const selectPermissionForManagingCustomFields = () =>
    checkPermission(DjangIO.app.models.PermissionType.can_access_custom_fields_management)
export const selectPermissionForStakeholder = () => checkPermission(DjangIO.app.models.PermissionType.stakeholder)

export const initialCommentFormValues = createSelector(
    selectInteractionLogger,
    selectDefaultCustomFields,
    (state, defaultCustomFields) => {
        const noteDetails = state.get("comment")

        // if we loaded an existing note, then use that to create our form's initial values
        if (noteDetails !== undefined) {
            const qdt = DjangIO.app.models.QuorumDataType.by_value(noteDetails.comment_type)
            const note_through_field =
                qdt.value === DjangIO.app.models.QuorumDataType.staffer.value
                    ? DjangIO.app.models.QuorumDataType.person.note_through_field
                    : qdt.note_through_field

            const isCommentTypeIssueProject =
                noteDetails.comment_type === DjangIO.app.models.QuorumDataType.project.value

            return {
                noteDetails,
                // This check is to account for a discrepancy between projects/issues and issue management
                // in the Comment Type select. This makes the form's type field `issue_management.value`
                // but the Comment is still patched as `project.value`
                type: isCommentTypeIssueProject
                    ? DjangIO.app.models.QuorumDataType.issue_management.value
                    : noteDetails.comment_type,
                datetime: moment(noteDetails.date, moment.ISO_8601),
                noteText: noteDetails.text,
                commentItems: getCacheJsonKeyIds(noteDetails, note_through_field),
                attachments: noteDetails.cache_json.attachments,
                projects:
                    noteDetails.projects && noteDetails.projects.length
                        ? getFieldIdsFromProjects(noteDetails.projects)
                        : [],
                ...(noteDetails.tag_dict || {}),
                is_visible_to_partners: noteDetails.is_visible_to_partners,
                limited_to_teams: noteDetails.limited_to_teams || [],
            }
        }

        // otherwise, we are making a new interaction note from scratch
        const type = getCommentTypeChoices().length
            ? getCommentTypeChoices()[0].value
            : DjangIO.app.models.QuorumDataType.staffer.value
        return {
            attachments: [],
            commentItems: [],
            is_visible_to_partners: getUserdataField("should_share_with_partners_by_default"),
            limited_to_teams: getUserdataField("default_limited_to_teams"),
            type,
            projects: [],
            ...defaultCustomFields,
        }
    },
)

export const selectIssueIsRequired = () => getUserdataField("organization_require_issue_on_interaction")
export const selectIssueSelectLabel = createSelector(
    selectIssueIsRequired,
    (issueIsRequired) => `Issues${issueIsRequired ? " (required)" : ""}`,
)

const allSelectors = (formKey) => ({
    // selectors that are agnostic to specific form type
    datumId: selectDatumId,
    isEdit: selectIsEdit,
    isEditing: selectIsEditing,
    isInvalid: createSelectIsInvalid(formKey),
    isLoading: selectIsLoading,
    isSubmitting: createSelectIsSubmitting(formKey),
    issueIsRequired: selectIssueIsRequired,
    issueSelectLabel: selectIssueSelectLabel,
    noteDetails: createSelectNoteDetails(formKey),
    officialLabel: selectOfficialLabel,
    officialPlaceholder: selectOfficialPlaceholder,
    officialFilterableListView: selectOfficialFilterableListView,
    projectOptions: selectProjectOptions,
    projects: selectArrayValue(formKey, "projects"),
    permissionForOfficials: selectPermissionForOfficials,
    permissionForStaffers: selectPermissionForStaffers,
    permissionForPressContacts: selectPermissionForPressContacts,
    permissionForCustomContacts: selectPermissionForCustomContacts,
    permissionForTaggingBill: selectPermissionForTaggingBill,
    permissionForExecutive: selectPermissionForExecutive,
    permissionForManagingCustomFields: selectPermissionForManagingCustomFields,
    permissionForStakeholder: selectPermissionForStakeholder,
    // selectors specific to a type of interaction logger form
    bills: createSelectBills(formKey),
    contactsParticipants: createSelectSupporterParticipants(formKey),
    customFields: selectCustomFieldsConnection(DjangIO.app.models.QuorumDataType.note.value),
    datetime: createSelectDatetime(formKey),
    formErrors: createSelectFormErrors(formKey),
    officialsParticipants: createSelectOfficialParticipants(formKey),
    orgsParticipants: createSelectPublicOrgParticipants(formKey),
    staffParticipants: createSelectStafferParticipants(formKey),
    pressContactParticipants: createSelectPressContactParticipants(formKey),
    teamMembersParticipants: createSelectTeamMemberParticipants(formKey),
    type: createSelectType(formKey),
    // Selectors to get the region of Interaction participants
    officialsMostRecentRegions: createSelectOfficialsMostRecentRegion(formKey),
    staffersMostRecentRegions: createSelectStaffersMostRecentRegion(formKey),
    contactsRegions: createSelectContactsRegion(formKey),
    commentsRegions: createSelectCommentsRegion(formKey),
})

// get all the OOB fields
// create a dictionary of selectors based on these fields
const getOOBJson = () => {
    const groups = getUserdataField("log_interaction_json")
    return groups ? groups.sort((a, b) => a.order - b.order) : []
}

const getOOBCustomFields = () => {
    const logInteractionJson = getOOBJson()
    const customFields = []
    if (logInteractionJson && logInteractionJson.length) {
        logInteractionJson.forEach((group) => {
            group.fields.forEach((field) => {
                // TODO: remove formattedField and just use field once Google's log_interaction_json is updated Aug 2023
                const formattedField = { ...field, ...field.props }
                if (formattedField.note_field_type === DjangIO.app.userdata.types.NoteFieldType.custom_field.value) {
                    customFields.push(formattedField.custom_field_slug)
                }
            })
        })
    }
    return customFields
}

const oobAllSelectors = (formKey) => {
    const allFieldSelectors = allSelectors(formKey)
    const oobCustomFields = getOOBCustomFields()

    if (oobCustomFields.length) {
        oobCustomFields.forEach((slug) => {
            const fieldKey = snakeToCamelCase(slug) + "Selector"
            allFieldSelectors[fieldKey] = selectLogInteractionFormValue(slug)
        })
    }
    allFieldSelectors["groups"] = getOOBJson
    allFieldSelectors["userTeams"] = selectTeams

    return allFieldSelectors
}

export const logInteractionFormConnection = createStructuredSelector({
    initialValues: initialInteractionFormValues,
    businessCards: selectBusinessCards,
    typeChoices: getInteractionTypeChoices,
    durationChoices: getDurationChoices,
    ...oobAllSelectors(LOG_INTERACTION_FORM_KEY),
})

export const logDonationFormConnection = createStructuredSelector({
    initialValues: initialDonationFormValues,
    typeChoices: getDonationTypeChoices,
    ...allSelectors(LOG_DONATION_FORM_KEY),
})

export const logRelationshipFormConnection = createStructuredSelector({
    initialValues: initialRelationshipFormValues,
    typeChoices: getRelationshipTypeChoices,
    ...allSelectors(LOG_RELATIONSHIP_FORM_KEY),
})

export const logCommentFormConnection = createStructuredSelector({
    initialValues: initialCommentFormValues,
    commentItems: selectCommentItems,
    typeChoices: getCommentTypeChoices,
    canTagProjectsToComment: selectCanTagProjectsToComment,
    ...allSelectors(LOG_COMMENT_FORM_KEY),
})

// sendUserAction text for user analytics
export const getUserActionDescription = (formKey, type) => {
    // Text for user analytics useraction
    let userActionText = ""
    switch (formKey) {
        case LOG_INTERACTION_FORM_KEY:
            userActionText = `${DjangIO.app.userdata.types.InteractionType.by_value(type).label}`
            break
        case LOG_DONATION_FORM_KEY:
            userActionText = `${DjangIO.app.userdata.types.InteractionType.by_value(type).label}`
            break
        case LOG_COMMENT_FORM_KEY:
            userActionText = `Comment on ${DjangIO.app.models.QuorumDataType.by_value(type).plural}`
            break
        case LOG_RELATIONSHIP_FORM_KEY:
            userActionText = `${DjangIO.app.userdata.types.RelationshipType.by_value(type).label}`
            break
        default:
            break
    }
    return userActionText
}

export const getUserActionDataTypesFromInteraction = (props) => {
    // For Comments, you can only add participants of a single type, stored under the type key
    if (props.form === LOG_COMMENT_FORM_KEY) {
        return [DjangIO.app.models.QuorumDataType.by_value(props.type)]
    }

    // For other Interactions, the participants are stored in a separate array per QDT
    const hasOfficial = Boolean(props.officialsParticipants && props.officialsParticipants.length)
    const hasStaffer = Boolean(props.staffParticipants && props.staffParticipants.length)
    const hasPressContact = Boolean(props.pressContactParticipants && props.pressContactParticipants.length)
    const hasSupporter = Boolean(props.contactsParticipants && props.contactsParticipants.length)
    const dataTypes = []

    if (hasOfficial) {
        dataTypes.push(DjangIO.app.models.QuorumDataType.person)
    }
    if (hasStaffer) {
        dataTypes.push(DjangIO.app.models.QuorumDataType.staffer)
    }
    if (hasPressContact) {
        dataTypes.push(DjangIO.app.models.QuorumDataType.press_contact)
    }
    if (hasSupporter) {
        dataTypes.push(DjangIO.app.models.QuorumDataType.supporter)
    }

    return dataTypes
}

// exclusively for mobile

export const createSelectIsDisabled = (reduxFormKey) =>
    createSelector(
        createSelectIsSubmitting(reduxFormKey),
        createSelectIsInvalid(reduxFormKey),
        (submitting, invalid) => submitting || invalid,
    )

export const createBackButtonSelectors = (reduxFormKey) =>
    createStructuredSelector({
        disabled: createSelectIsSubmitting(reduxFormKey),
    })

export const createSubmitButtonSelectors = (reduxFormKey) =>
    createStructuredSelector({
        disabled: createSelectIsDisabled(reduxFormKey),
        isSubmitting: createSelectIsSubmitting(reduxFormKey),
        form: () => reduxFormKey,
    })
